import {
  Button,
  Center,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { Warning } from '@cronos-app/icons';
import { currentWallet } from '@cronos-app/wallet';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const DoubleCheck = ({
  isOpen,
  onClose,
  callback,
}: {
  isOpen: boolean;
  onClose: () => void;
  callback: () => void;
}) => {
  const { confirm } = useDoubleCheck();
  const t = useTranslations();
  return (
    <Modal size="sm" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent p={0}>
        <ModalHeader>
          <Text
            bg="gradients.white-blue-title"
            bgClip="text"
            textStyle={{
              base: 'body1Bold',
              desktop: 'subtitle3',
            }}
          >
            {t('card.Confirm topping up?')}
          </Text>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody
          p="6"
          pt="0"
          display="flex"
          flexDir="column"
          color="brand.white"
          gap="4"
        >
          <Center>
            <Icon
              as={Warning}
              w="50px"
              h="50px"
              color="#FBBF10"
              sx={{
                path: {
                  fill: '#FBBF10',
                },
              }}
            />
          </Center>
          <Text textStyle="body2" textAlign="center">
            {t.rich('card.top-up-double-check', {
              source: (c) => (
                <Link
                  href="https://vvs.finance/"
                  isExternal
                  color="brand.secondary-sky-blue"
                >
                  {c}
                </Link>
              ),
              sourceName: 'VVS Finance',
              cdcLink: (c) => (
                <Link
                  href="https://crypto.com/"
                  isExternal
                  color="brand.secondary-sky-blue"
                >
                  {c}
                </Link>
              ),
            })}
          </Text>
          <Button
            w="full"
            size="sm"
            variant="ghost"
            bgColor="#159DFF"
            rounded="4px"
            textTransform="unset"
            onClick={() => {
              confirm();
              onClose();
              callback();
            }}
          >
            {t('card.I agree and acknowledge')}
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const useDoubleCheck = () => {
  const account = currentWallet.useAccount();
  const { confirmedByWallet } = useDoubleCheckStore();
  const isConfirmed = account ? confirmedByWallet[account] ?? false : false;
  const confirm = useCallback(() => {
    if (account) {
      useDoubleCheckStore.setState((state) => {
        return {
          confirmedByWallet: {
            ...state.confirmedByWallet,
            [account]: true,
          },
        };
      });
    }
  }, [account]);
  return {
    isConfirmed,
    confirm,
  };
};

const useDoubleCheckStore = create(
  persist<{
    confirmedByWallet: Record<string, boolean | undefined>;
  }>(
    () => ({
      confirmedByWallet: {},
    }),
    {
      name: '__TOP_UP_CONFIRMED_BY_WALLET__',
      version: 0,
    },
  ),
);
